import React, { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Users, Mail, CreditCard, UserPlus, Calendar, ExternalLink, Plus, X, Check, Globe, Target, Package, Pencil, Save } from 'lucide-react'
import { toast } from 'sonner'
import type { ClientTeamForm, Product, Territory, TeamMemberAssignment } from '@/types/client'

interface ClientTeamStepProps {
  data: ClientTeamForm
  onChange: (data: Partial<ClientTeamForm>) => void
  isLoading?: boolean
  productData?: { products: Product[], territories: Territory[] }
}

// Predefined options

export function ClientTeamStep({ data, onChange, isLoading, productData }: ClientTeamStepProps) {
  console.log("ClientTeamStep productData:", productData)

  // State for new team member form
  const [newTeamMemberName, setNewTeamMemberName] = useState('')
  const [newTeamMemberEmail, setNewTeamMemberEmail] = useState('')
  const [newTeamMemberTitle, setNewTeamMemberTitle] = useState('')
  const [newTeamMemberCalendly, setNewTeamMemberCalendly] = useState('')
  
  // State for assignments
  const [selectedProducts, setSelectedProducts] = useState<string[]>([])
  const [selectedTerritories, setSelectedTerritories] = useState<string[]>([])

  // State for meeting links
  const [newMeetingLinkName, setNewMeetingLinkName] = useState('')
  const [newMeetingLinkUrl, setNewMeetingLinkUrl] = useState('')

  // State for editing team members
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null)
  const [editMemberName, setEditMemberName] = useState('')
  const [editMemberEmail, setEditMemberEmail] = useState('')
  const [editMemberTitle, setEditMemberTitle] = useState('')
  const [editMemberCalendly, setEditMemberCalendly] = useState('')
  const [editSelectedProducts, setEditSelectedProducts] = useState<string[]>([])
  const [editSelectedTerritories, setEditSelectedTerritories] = useState<string[]>([])

  const handleInputChange = (field: keyof ClientTeamForm, value: any) => {
    onChange({ [field]: value })
  }

  const handleAddTeamMember = () => {
    if (!newTeamMemberEmail || !newTeamMemberName) {
      toast.error('Please enter email and name')
      return
    }

    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product')
      return
    }

    // Check for existing email in team_members array (primary source)
    // Only check legacy sales_team_emails if team_members exists (to avoid stale data conflicts)
    const existingMemberEmails = (data.team_members || []).map(m => m.email?.toLowerCase().trim()).filter(Boolean)
    const legacyEmails = (data.team_members && data.team_members.length > 0) 
      ? (data.sales_team_emails || []).map(e => e?.toLowerCase().trim()).filter(Boolean)
      : []
    const allExistingEmails = [...new Set([...existingMemberEmails, ...legacyEmails])]
    
    if (allExistingEmails.includes(newTeamMemberEmail.toLowerCase().trim())) {
      toast.error('Email already exists in team')
      return
    }

    // Create assignments for each product-territory combination
    const assignments: TeamMemberAssignment[] = []
    const hasTerritories = productData?.territories && productData.territories.length > 0
    
    if (hasTerritories && selectedTerritories.length > 0) {
      // If territories exist and are selected, create assignments for each product-territory combination
      selectedProducts.forEach(productId => {
        selectedTerritories.forEach(territoryId => {
          assignments.push({
            id: `assignment-${Date.now()}-${Math.random()}`,
            team_member_id: '', // Will be set after team member is created
            product_id: productId,
            territory_id: territoryId,
          })
        })
      })
    } else {
      // If no territories selected, create assignments for each product without specific territory
      selectedProducts.forEach(productId => {
        assignments.push({
          id: `assignment-${Date.now()}-${Math.random()}`,
          team_member_id: '', // Will be set after team member is created
          product_id: productId,
          territory_id: '', // No specific territory
        })
      })
    }

    // Add team member to legacy arrays for backward compatibility
    const updatedTeamMembers = [...(data.team_members || []), {
      id: crypto.randomUUID(),
      name: newTeamMemberName,
      email: newTeamMemberEmail,
        role: 'Other' as const, // Default role for backward compatibility
        calendly_link: newTeamMemberCalendly,
        languages_spoken: [],
        capacity: { max_leads_per_month: 0, current_load: 0 },
        ...(newTeamMemberTitle && { title: newTeamMemberTitle } as any)
    }]

    // Update assignments with team member ID
    const finalAssignments = assignments.map(assignment => ({
      ...assignment,
      team_member_id: updatedTeamMembers[updatedTeamMembers.length - 1].id
    }))

    onChange({
      team_members: updatedTeamMembers,
      assignments: [...(data.assignments || []), ...finalAssignments],
      sdr_count: 0, // No longer tracking by role
      ae_count: 0, // No longer tracking by role
      // Legacy compatibility
      sales_team_emails: [...(data.sales_team_emails || []), newTeamMemberEmail],
      sales_team_names: [...(data.sales_team_names || []), newTeamMemberName],
      sales_team_roles: [...(data.sales_team_roles || []), newTeamMemberTitle || 'Other'],
      sales_team_regions: [...(data.sales_team_regions || []), ''],
      sales_team_calendly_links: [...(data.sales_team_calendly_links || []), newTeamMemberCalendly],
    })

    // Reset form
    setNewTeamMemberName('')
    setNewTeamMemberEmail('')
    setNewTeamMemberTitle('')
    setNewTeamMemberCalendly('')
    setSelectedProducts([])
    setSelectedTerritories([])
    
    toast.success('Team member added successfully!')
  }

  const handleEditTeamMember = (memberId: string) => {
    const member = data.team_members?.find(m => m.id === memberId)
    if (!member) return

    // Get existing assignments for this member
    const memberAssignments = data.assignments?.filter(a => a.team_member_id === memberId) || []
    
    // Populate edit form with member data
    setEditMemberName(member.name)
    setEditMemberEmail(member.email)
    setEditMemberTitle((member as any).title || '')
    setEditMemberCalendly(member.calendly_link || '')
    
    // Get unique products and territories from assignments
    const assignedProductIds = [...new Set(memberAssignments.map(a => a.product_id).filter(Boolean))]
    const assignedTerritoryIds = [...new Set(memberAssignments.map(a => a.territory_id).filter(Boolean))]
    
    setEditSelectedProducts(assignedProductIds)
    setEditSelectedTerritories(assignedTerritoryIds)
    
    setEditingMemberId(memberId)
  }

  const handleSaveEdit = () => {
    if (!editingMemberId) return

    if (!editMemberEmail || !editMemberName) {
      toast.error('Please enter email and name')
      return
    }

    if (editSelectedProducts.length === 0) {
      toast.error('Please select at least one product')
      return
    }

    // Check if email is already used by another member (case-insensitive)
    const otherMembers = data.team_members?.filter(m => m.id !== editingMemberId) || []
    const existingEmails = otherMembers.map(m => m.email?.toLowerCase().trim()).filter(Boolean)
    const legacyEmails = (data.sales_team_emails || []).map(e => e?.toLowerCase().trim()).filter(Boolean)
    const allExistingEmails = [...new Set([...existingEmails, ...legacyEmails])]
    
    if (allExistingEmails.includes(editMemberEmail.toLowerCase().trim())) {
      toast.error('Email already exists in team')
      return
    }

    // Remove old assignments for this member
    const otherAssignments = data.assignments?.filter(a => a.team_member_id !== editingMemberId) || []

    // Create new assignments for each product-territory combination
    const newAssignments: TeamMemberAssignment[] = []
    const hasTerritories = productData?.territories && productData.territories.length > 0
    
    if (hasTerritories && editSelectedTerritories.length > 0) {
      editSelectedProducts.forEach(productId => {
        editSelectedTerritories.forEach(territoryId => {
          newAssignments.push({
            id: `assignment-${Date.now()}-${Math.random()}`,
            team_member_id: editingMemberId,
            product_id: productId,
            territory_id: territoryId,
          })
        })
      })
    } else {
      editSelectedProducts.forEach(productId => {
        newAssignments.push({
          id: `assignment-${Date.now()}-${Math.random()}`,
          team_member_id: editingMemberId,
          product_id: productId,
          territory_id: '', // No specific territory
        })
      })
    }

    // Update team member
    const updatedTeamMembers = (data.team_members || []).map(member => 
      member.id === editingMemberId ? {
        ...member,
        name: editMemberName,
        email: editMemberEmail,
        role: 'Other' as const, // Default role for backward compatibility
        calendly_link: editMemberCalendly,
        ...(editMemberTitle && { title: editMemberTitle } as any)
      } : member
    )

    // Update legacy arrays
    const allEmails = updatedTeamMembers.map(m => m.email)
    const allNames = updatedTeamMembers.map(m => m.name)
    const allRoles = updatedTeamMembers.map(m => (m as any).title || 'Other')
    const allRegions = updatedTeamMembers.map(m => '')
    const allCalendlyLinks = updatedTeamMembers.map(m => m.calendly_link || '')

    onChange({
      team_members: updatedTeamMembers,
      assignments: [...otherAssignments, ...newAssignments],
      sdr_count: 0, // No longer tracking by role
      ae_count: 0, // No longer tracking by role
      // Legacy compatibility
      sales_team_emails: allEmails,
      sales_team_names: allNames,
      sales_team_roles: allRoles,
      sales_team_regions: allRegions,
      sales_team_calendly_links: allCalendlyLinks,
    })

    // Reset edit state
    setEditingMemberId(null)
    setEditMemberName('')
    setEditMemberEmail('')
    setEditMemberTitle('')
    setEditMemberCalendly('')
    setEditSelectedProducts([])
    setEditSelectedTerritories([])

    toast.success('Team member updated successfully!')
  }

  const handleCancelEdit = () => {
    setEditingMemberId(null)
    setEditMemberName('')
    setEditMemberEmail('')
    setEditMemberTitle('')
    setEditMemberCalendly('')
    setEditSelectedProducts([])
  }

  const handleRemoveTeamMember = (memberId: string) => {
    const updatedTeamMembers = (data.team_members || []).filter(member => member.id !== memberId)
    const updatedAssignments = (data.assignments || []).filter(assignment => assignment.team_member_id !== memberId)
    
    onChange({
      team_members: updatedTeamMembers,
      assignments: updatedAssignments,
      sdr_count: 0, // No longer tracking by role
      ae_count: 0, // No longer tracking by role
    })

    toast.success('Team member removed!')
  }

  const handleAddMeetingLink = () => {
    if (!newMeetingLinkName || !newMeetingLinkUrl) {
      toast.error('Please enter both name and URL')
      return
    }

    const currentLinks = data.meeting_links || {}
    if (currentLinks[newMeetingLinkName]) {
      toast.error('Meeting link name already exists')
      return
    }

    onChange({
      meeting_links: { ...currentLinks, [newMeetingLinkName]: newMeetingLinkUrl }
    })

    setNewMeetingLinkName('')
    setNewMeetingLinkUrl('')
    toast.success('Meeting link added!')
  }

  const handleRemoveMeetingLink = (name: string) => {
    const currentLinks = data.meeting_links || {}
    const { [name]: removed, ...remainingLinks } = currentLinks
    onChange({ meeting_links: remainingLinks })
    toast.success('Meeting link removed!')
  }

  return (
    <div className="space-y-2">
      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
          <CardDescription>Primary and secondary contact details for this client updates</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Main Contact */}
          <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
            <Label className="text-sm font-semibold">Main Contact</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="main_contact_name">Name</Label>
                <Input
                  id="main_contact_name"
                  type="text"
                  value={data.main_contact_name || ''}
                  onChange={(e) => handleInputChange('main_contact_name', e.target.value)}
                  placeholder="John Smith"
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="main_contact_email">Email</Label>
                <Input
                  id="main_contact_email"
                  type="email"
                  value={data.main_contact_email || ''}
                  onChange={(e) => handleInputChange('main_contact_email', e.target.value)}
                  placeholder="main@company.com"
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="main_contact_phone">Phone</Label>
                <Input
                  id="main_contact_phone"
                  type="tel"
                  value={data.main_contact_phone || ''}
                  onChange={(e) => handleInputChange('main_contact_phone', e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {/* Secondary Contact */}
          <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
            <Label className="text-sm font-semibold">Secondary Contact</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="secondary_contact_name">Name</Label>
                <Input
                  id="secondary_contact_name"
                  type="text"
                  value={data.secondary_contact_name || ''}
                  onChange={(e) => handleInputChange('secondary_contact_name', e.target.value)}
                  placeholder="Jane Doe"
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="secondary_contact_email">Email</Label>
                <Input
                  id="secondary_contact_email"
                  type="email"
                  value={data.secondary_contact_email || ''}
                  onChange={(e) => handleInputChange('secondary_contact_email', e.target.value)}
                  placeholder="secondary@company.com"
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="secondary_contact_phone">Phone</Label>
                <Input
                  id="secondary_contact_phone"
                  type="tel"
                  value={data.secondary_contact_phone || ''}
                  onChange={(e) => handleInputChange('secondary_contact_phone', e.target.value)}
                  placeholder="+1 (555) 123-4568"
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billing Information */}
      <Card>
        <CardHeader>
          <CardTitle>Billing Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="billing_name">Billing Contact Name</Label>
              <Input
                id="billing_name"
                value={data.billing_name || ''}
                onChange={(e) => handleInputChange('billing_name', e.target.value)}
                placeholder="John Smith"
                disabled={isLoading}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="billing_email">Billing Email</Label>
              <Input
                id="billing_email"
                type="email"
                value={data.billing_email || ''}
                onChange={(e) => handleInputChange('billing_email', e.target.value)}
                placeholder="billing@company.com"
                disabled={isLoading}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add New Team Member */}
      <Card>
        <CardHeader>
          <CardTitle>Add New Team Member</CardTitle>
          <CardDescription>
            Add individual sales team members with their product and territory assignments
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="new_team_member_name">Full Name</Label>
              <Input
                id="new_team_member_name"
                value={newTeamMemberName}
                onChange={(e) => setNewTeamMemberName(e.target.value)}
                placeholder="John Smith"
                disabled={isLoading}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="new_team_member_email">Email Address</Label>
              <Input
                id="new_team_member_email"
                type="email"
                value={newTeamMemberEmail}
                onChange={(e) => setNewTeamMemberEmail(e.target.value)}
                placeholder="john@company.com"
                disabled={isLoading}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="new_team_member_title">Title</Label>
              <Input
                id="new_team_member_title"
                value={newTeamMemberTitle}
                onChange={(e) => setNewTeamMemberTitle(e.target.value)}
                placeholder="CEO, Marketing Director, etc."
                disabled={isLoading}
                className="h-9"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="new_team_member_calendly">Personal Calendly Link</Label>
              <Input
                id="new_team_member_calendly"
                type="url"
                value={newTeamMemberCalendly}
                onChange={(e) => setNewTeamMemberCalendly(e.target.value)}
                placeholder="https://calendly.com/john-smith"
                disabled={isLoading}
                className="h-9"
              />
              <p className="text-sm text-muted-foreground">
                Optional: Individual Calendly link for direct scheduling with this team member
              </p>
            </div>
          </div>

          {/* Product & Territory Assignments */}
          <div className="space-y-3 pt-3 border-t border-border">
            <div className="flex items-center space-x-2">
              <Target className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Product & Territory Assignments</CardTitle>
            </div>
            <CardDescription>
              Select which products and territories this team member will handle
            </CardDescription>

            {productData && productData.products && productData.products.length > 0 ? (
              <div className="space-y-2">
                {/* Product Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center space-x-1">
                    <Package className="h-4 w-4" />
                    <span>Products</span>
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {productData.products.map(product => {
                      const isSelected = selectedProducts.includes(product.id)
                      return (
                        <div 
                          key={product.id} 
                          className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer select-none"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            if (isSelected) {
                              setSelectedProducts(selectedProducts.filter(id => id !== product.id))
                            } else {
                              setSelectedProducts([...selectedProducts, product.id])
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            id={`product-${product.id}`}
                            checked={isSelected}
                            readOnly
                            className="form-checkbox h-4 w-4 text-primary rounded cursor-pointer pointer-events-none"
                          />
                          <Label htmlFor={`product-${product.id}`} className="text-sm cursor-pointer flex-1 pointer-events-none">
                            <div className="font-medium">{product.name}</div>
                            {product.category && (
                              <div className="text-xs text-muted-foreground">{product.category}</div>
                            )}
                          </Label>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Territory Selection */}
                {productData.territories && productData.territories.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium flex items-center space-x-1">
                      <Target className="h-4 w-4" />
                      <span>Territories</span>
                    </Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {productData.territories.map(territory => {
                        const isSelected = selectedTerritories.includes(territory.id)
                        return (
                          <div 
                            key={territory.id} 
                            className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer select-none"
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              if (isSelected) {
                                setSelectedTerritories(selectedTerritories.filter(id => id !== territory.id))
                              } else {
                                setSelectedTerritories([...selectedTerritories, territory.id])
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              id={`territory-${territory.id}`}
                              checked={isSelected}
                              readOnly
                              className="form-checkbox h-4 w-4 text-primary rounded cursor-pointer pointer-events-none"
                            />
                            <Label htmlFor={`territory-${territory.id}`} className="text-sm cursor-pointer flex-1 pointer-events-none">
                              <div className="font-medium">{territory.name}</div>
                            </Label>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No products or territories defined yet.</p>
                <p className="text-sm">Please complete Step 2: Products & Territories first.</p>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button
                onClick={handleAddTeamMember}
                disabled={isLoading}
                className="px-6 h-9 text-sm"
                size="sm"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Team Member
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Existing Team Members */}
      <Card>
        <CardHeader>
          <CardTitle>Current Team Members</CardTitle>
          <CardDescription>
            Manage your current sales and account management team
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {data.team_members && data.team_members.length > 0 ? (
            <div className="space-y-2">
              {data.team_members.map((member) => (
                <div key={member.id} className="p-3 border rounded bg-card hover:bg-muted/50 transition-colors">
                  {editingMemberId === member.id ? (
                    // Edit Mode
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label>Full Name</Label>
                          <Input
                            value={editMemberName}
                            onChange={(e) => setEditMemberName(e.target.value)}
                            disabled={isLoading}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>Email Address</Label>
                          <Input
                            type="email"
                            value={editMemberEmail}
                            onChange={(e) => setEditMemberEmail(e.target.value)}
                            disabled={isLoading}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>Title</Label>
                          <Input
                            value={editMemberTitle}
                            onChange={(e) => setEditMemberTitle(e.target.value)}
                            placeholder="CEO, Marketing Director, etc."
                            disabled={isLoading}
                          />
                        </div>
                        <div className="space-y-1 md:col-span-2">
                          <Label>Personal Calendly Link</Label>
                          <Input
                            type="url"
                            value={editMemberCalendly}
                            onChange={(e) => setEditMemberCalendly(e.target.value)}
                            placeholder="https://calendly.com/john-smith"
                            disabled={isLoading}
                          />
                        </div>
                      </div>

                      {productData && productData.products && productData.products.length > 0 && (
                        <div className="space-y-3 pt-2 border-t">
                          <Label className="text-sm font-medium">Product & Territory Assignments</Label>
                          
                          {/* Product Selection */}
                          <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Products</Label>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                              {productData.products.map(product => {
                                const isSelected = editSelectedProducts.includes(product.id)
                                return (
                                  <div 
                                    key={product.id} 
                                    className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-muted/50 select-none"
                                    onClick={(e) => {
                                      if (!isLoading) {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        if (isSelected) {
                                          setEditSelectedProducts(editSelectedProducts.filter(id => id !== product.id))
                                        } else {
                                          setEditSelectedProducts([...editSelectedProducts, product.id])
                                        }
                                      }
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      readOnly
                                      className="form-checkbox h-4 w-4 text-primary rounded cursor-pointer pointer-events-none"
                                      disabled={isLoading}
                                    />
                                    <Label className="text-sm cursor-pointer flex-1 pointer-events-none">{product.name}</Label>
                                  </div>
                                )
                              })}
                            </div>
                          </div>

                          {/* Territory Selection */}
                          {productData.territories && productData.territories.length > 0 && (
                            <div className="space-y-2">
                              <Label className="text-xs text-muted-foreground">Territories</Label>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {productData.territories.map(territory => {
                                  const isSelected = editSelectedTerritories.includes(territory.id)
                                  return (
                                    <div 
                                      key={territory.id} 
                                      className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-muted/50 select-none"
                                      onClick={(e) => {
                                        if (!isLoading) {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          if (isSelected) {
                                            setEditSelectedTerritories(editSelectedTerritories.filter(id => id !== territory.id))
                                          } else {
                                            setEditSelectedTerritories([...editSelectedTerritories, territory.id])
                                          }
                                        }
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        readOnly
                                        className="form-checkbox h-4 w-4 text-primary rounded cursor-pointer pointer-events-none"
                                        disabled={isLoading}
                                      />
                                      <Label className="text-sm cursor-pointer flex-1 pointer-events-none">{territory.name}</Label>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}

                        </div>
                      )}

                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCancelEdit}
                          disabled={isLoading}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveEdit}
                          disabled={isLoading}
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Display Mode
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center space-x-2">
                          <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <div>
                            <div className="font-semibold text-base">{member.name}</div>
                            <div className="text-sm text-muted-foreground">{member.email}</div>
                            {(member as any).title && <div className="text-sm text-muted-foreground">{(member as any).title}</div>}
                          </div>
                        </div>

                        {/* Display Assignments */}
                        {data.assignments && data.assignments.filter(a => a.team_member_id === member.id).length > 0 && (
                          <div className="space-y-1">
                            <div className="text-sm font-medium">Assignments:</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {data.assignments
                                .filter(a => a.team_member_id === member.id)
                                .map(assignment => {
                                  const product = productData?.products?.find(p => p.id === assignment.product_id)
                                  const territory = productData?.territories?.find(t => t.id === assignment.territory_id)
                                  return (
                                    <div key={assignment.id} className="p-1 bg-muted/20 rounded text-xs">
                                      <div className="font-medium">
                                        {product?.name || 'Unknown Product'}
                                        {territory && <span className="text-muted-foreground"> / {territory.name}</span>}
                                      </div>
                                    </div>
                                  )
                                })}
                            </div>
                          </div>
                        )}

                        {member.calendly_link && (
                          <div className="pt-2">
                            <a
                              href={member.calendly_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                            >
                              <Calendar className="h-4 w-4" />
                              Schedule Meeting
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditTeamMember(member.id)}
                          disabled={isLoading}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveTeamMember(member.id)}
                          disabled={isLoading}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No team members added yet.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Meeting Links */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-primary" />
            <CardTitle>Meeting Links</CardTitle>
          </div>
          <CardDescription>
            Add general meeting links for the sales team
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 border rounded bg-muted/50">
            <div className="space-y-1">
              <Label htmlFor="new_meeting_link_name">Link Name</Label>
              <Input
                id="new_meeting_link_name"
                value={newMeetingLinkName}
                onChange={(e) => setNewMeetingLinkName(e.target.value)}
                placeholder="Demo Call"
                disabled={isLoading}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="new_meeting_link_url">URL</Label>
              <Input
                id="new_meeting_link_url"
                type="url"
                value={newMeetingLinkUrl}
                onChange={(e) => setNewMeetingLinkUrl(e.target.value)}
                placeholder="https://calendly.com/company/demo"
                disabled={isLoading}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleAddMeetingLink} disabled={isLoading} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Link
              </Button>
            </div>
          </div>

          {data.meeting_links && Object.keys(data.meeting_links).length > 0 && (
            <div className="space-y-1">
              <Label>Current Meeting Links</Label>
              <div className="space-y-1">
                {Object.entries(data.meeting_links).map(([name, url]) => (
                  <div key={name} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <ExternalLink className="h-4 w-4 text-blue-600" />
                      <div>
                        <div className="font-medium">{name}</div>
                        <div className="text-sm text-muted-foreground">{url}</div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveMeetingLink(name)}
                      disabled={isLoading}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-center space-x-2 text-blue-800">
            <Users className="h-4 w-4" />
            <div className="text-sm">
              <p className="font-medium">Team Summary</p>
              <p>
                {data.team_members?.length || 0} team members • {Object.keys(data.meeting_links || {}).length} meeting links
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
